import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import {
  LayoutDashboard, Settings, Star, Image as ImgIcon,
  ShoppingBag, Tag, Phone as PhoneIcon, Sparkles, Calendar,
  MessageSquare, LogOut, ExternalLink, KeyRound, Menu as MenuIcon,
  Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/identity', label: 'Site Identity', icon: Settings },
  { to: '/admin/hero', label: 'Hero Section', icon: Sparkles },
  { to: '/admin/services', label: 'Services', icon: Star },
  { to: '/admin/pricing', label: 'Pricing', icon: Tag },
  { to: '/admin/gallery', label: 'Gallery', icon: ImgIcon },
  { to: '/admin/products', label: 'Products', icon: ShoppingBag },
  { to: '/admin/contact', label: 'Contact & Footer', icon: PhoneIcon },
  { to: '/admin/bookings', label: 'Bookings', icon: Calendar },
  { to: '/admin/messages', label: 'Messages', icon: MessageSquare },
  { to: '/admin/smtp', label: 'Email Settings', icon: Mail },
  { to: '/admin/password', label: 'Change Password', icon: KeyRound },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { config } = useSiteConfig();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = React.useState(false);

  const onLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform ${
          mobileNav ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-5 border-b border-gray-100">
          <Link to="/admin" className="flex items-center gap-3">
            {config.identity?.logo && (
              <img src={config.identity.logo} alt="" className="w-10 h-10 object-contain" style={{ mixBlendMode: 'multiply' }} />
            )}
            <div className="font-display text-base text-[#5a1d3b] leading-tight" style={{ fontWeight: 500 }}>
              {config.identity?.site_name?.split(' ').slice(0, 2).join(' ') || 'Dalaa Beauty'}
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-normal">
                Admin Panel
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setMobileNav(false)}
                data-testid={`admin-nav-${it.to.replace('/admin', '').replace('/', '') || 'dashboard'}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? 'bg-[#fdf2f5] text-[#c4477a] font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.7} />
                {it.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-100 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={1.7} />
            View Site
          </a>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 h-14 flex items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 -ml-2 text-gray-700"
              onClick={() => setMobileNav((v) => !v)}
            >
              <MenuIcon className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-500">Welcome, <span className="font-medium text-gray-700">{user?.username}</span></span>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-gray-600">
            <LogOut className="w-4 h-4 mr-1.5" /> Logout
          </Button>
        </header>

        <main className="flex-1 p-5 lg:p-8">
          <Outlet />
        </main>
      </div>

      {mobileNav && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setMobileNav(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
